/**
 * AsnLocationDetailsTable.tsx
 * Web table orchestrator for ASN Location Details.
 *
 * Reads the API response (rows/grandTotal/effectiveGrainKeys/pagination),
 * loading, and error state directly from the `asnLocationSummary` Redux
 * slice — no duplicate component state. Sort/page/page-size interactions
 * are bubbled up as callbacks to `LocationSummary`, which owns the actual
 * `POST /api/asn/location-details` fetch (it has access to the shared
 * filter-bar values this table does not).
 */

import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import AsnOnePagerTable from './AsnOnePagerTable';
import { ASN_METRIC_COLUMNS, buildAsnGrandTotalMetricValues, buildAsnGroupedRowFromApi } from '../constants/asnOnePagerConstants';
import {
  convertAsnMetricsToPagerColumns,
  convertAsnRowToGroupedRow,
  convertAsnRowsToGroupedRows,
} from '../utils/asnLocationDetailsAdapter';

export interface AsnLocationDetailsTableProps {
  /** Bubbles up to LocationSummary, which dispatches the new sort + refetches. */
  readonly onSort: (col: string) => void;
  /** Zero-based page number. */
  readonly onPageChange: (pageNumber: number) => void;
  readonly onPageSizeChange: (pageSize: number) => void;
  /** When provided, only metric columns whose `id` is in this set are rendered. */
  readonly visibleMetricColumnIds?: Set<string>;
  /** When provided, only grain keys in this set are rendered as sticky columns. */
  readonly visibleGrainKeys?: Set<string>;
  /** Export callback — forwarded to AsnOnePagerTable's section header button. */
  readonly onExport?: () => void;
  readonly isExporting?: boolean;
  /** Search box rendered inside the card header. */
  readonly searchValue?: string;
  readonly onSearchChange?: (text: string) => void;
  /** Ref attached to the Columns button for dropdown positioning. */
  readonly columnsButtonRef?: React.RefObject<any>;
  readonly onColumnsPress?: () => void;
  /** Current perspective for the table heading — "shipping" or "receiving". */
  readonly perspective?: 'shipping' | 'receiving';
}

const AsnLocationDetailsTable: React.FC<AsnLocationDetailsTableProps> = ({
  onSort,
  onPageChange,
  onPageSizeChange,
  visibleMetricColumnIds,
  visibleGrainKeys,
  onExport,
  isExporting = false,
  searchValue,
  onSearchChange,
  columnsButtonRef,
  onColumnsPress,
  perspective,
}) => {
  const grainSelections = useSelector((state: RootState) => state.asnLocationSummary.grainSelections);
  const data = useSelector((state: RootState) => state.asnLocationSummary.data);
  const loading = useSelector((state: RootState) => state.asnLocationSummary.loading);
  const error = useSelector((state: RootState) => state.asnLocationSummary.error);
  const sortBy = useSelector((state: RootState) => state.asnLocationSummary.sortBy);
  const sortDirection = useSelector((state: RootState) => state.asnLocationSummary.sortDirection);
  const pageNumber = useSelector((state: RootState) => state.asnLocationSummary.pageNumber);
  const pageSize = useSelector((state: RootState) => state.asnLocationSummary.pageSize);

  // Metric columns never change at runtime — computed once, never re-created
  // per render (no stale memoized *grain* columns risk, since those are
  // recomputed from `effectiveGrainKeys` below on every data change).
  const pagerColumns = useMemo(() => convertAsnMetricsToPagerColumns(ASN_METRIC_COLUMNS), []);

  // The backend may reorder/collapse the requested grainSelections (e.g.
  // perspective-aware reordering) — render using what it actually grouped
  // by, falling back to the committed selection before the first response
  // arrives.
  const effectiveGrainKeys = data?.effectiveGrainKeys?.length ? data.effectiveGrainKeys : grainSelections;

  // Apply column-picker visibility filters — when a filter set is provided
  // only the matching columns/grains are forwarded to the table.
  const visiblePagerColumns = useMemo(
    () =>
      visibleMetricColumnIds
        ? pagerColumns.filter((col) => visibleMetricColumnIds.has(col.id))
        : pagerColumns,
    [pagerColumns, visibleMetricColumnIds],
  );
  const visibleEffectiveGrainKeys = useMemo(
    () =>
      visibleGrainKeys
        ? effectiveGrainKeys.filter((k) => visibleGrainKeys.has(k))
        : effectiveGrainKeys,
    [effectiveGrainKeys, visibleGrainKeys],
  );

  const groupedRows = useMemo(() => {
    if (!data?.rows?.length) return [];
    return convertAsnRowsToGroupedRows(data.rows.map(buildAsnGroupedRowFromApi));
  }, [data?.rows]);

  const grandTotalRow = useMemo(() => {
    if (!data?.grandTotal) return null;
    return convertAsnRowToGroupedRow({
      key: '__grand_total__',
      grainVals: {},
      memberCount: NaN,
      vals: buildAsnGrandTotalMetricValues(data.grandTotal),
    });
  }, [data?.grandTotal]);

  const pagination = data?.pagination;

  return (
    <View style={{ flex: 1 }}>
      <AsnOnePagerTable
        grainSelections={[...visibleEffectiveGrainKeys]}
        rows={groupedRows}
        columns={visiblePagerColumns}
        grandTotalRow={grandTotalRow}
        sort={{ col: sortBy, dir: sortDirection }}
        loading={loading}
        error={error}
        onSort={onSort}
        onNavigateToOverview={() => {}}
        onExport={onExport}
        isExporting={isExporting}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        columnsButtonRef={columnsButtonRef}
        onColumnsPress={onColumnsPress}
        totalRows={pagination?.totalRowCount ?? 0}
        totalPages={pagination?.totalPages ?? 0}
        page={(pagination?.pageIndex ?? pageNumber) + 1}
        pageSize={pagination?.pageSize ?? pageSize}
        onPageChange={(uiPage) => onPageChange(uiPage - 1)}
        onPageSizeChange={onPageSizeChange}
        perspective={perspective}
      />
    </View>
  );
};

AsnLocationDetailsTable.displayName = 'AsnLocationDetailsTable';

export default React.memo(AsnLocationDetailsTable);

